/** Title, second line and status dot for the hub's notification and approval rows (spec §4, §5). */
import type { NotificationRowData, PendingApproval } from "@/api/notifications";
import type { StatusTone } from "@/components/ui";
import { joinSecondLine } from "@/components/panels/search-modal/hub-results";
import { getErrorKindWords } from "@/lib/error-kind-labels";
import { formatRelativeTime, translate } from "@/i18n";
import { panelsNotificationsMessages } from "@/i18n/messages/panels-notifications";
import { markdownToPlainText } from "@/lib/markdown-plain-text";

export interface RowWords {
  title: string;
  secondLine: string;
  tone: StatusTone | null;
}

const QUESTION_KINDS: ReadonlySet<string> = new Set([
  "memory_review",
  "task_blocked",
]);
const FAILURE_KINDS: ReadonlySet<string> = new Set([
  "cli_error",
  "execution_failed",
]);

/** Joins the parts, dropping any that would repeat the title. */
function secondLineWithoutEcho(
  title: string,
  parts: Array<string | null | undefined>,
): string {
  const echo = title.trim().toLowerCase();
  return joinSecondLine(
    ...parts.filter((part) => part?.trim().toLowerCase() !== echo),
  );
}

export function notificationRowWords(row: NotificationRowData): RowWords {
  const repeats =
    row.occurrences > 1
      ? translate(panelsNotificationsMessages, "times", {
          count: row.occurrences,
        })
      : null;
  const time = formatRelativeTime(row.latest_at, { style: "narrow" });
  if (FAILURE_KINDS.has(row.kind)) {
    const words = getErrorKindWords({
      errorKind: row.error_kind,
      title: row.title,
      message: row.description || row.title,
      where: row.target?.label ?? null,
    });
    return {
      title: words.title,
      secondLine: secondLineWithoutEcho(words.title, [
        row.project_name,
        words.cause,
        repeats,
        time,
      ]),
      tone: row.read ? null : words.failed ? "error" : "unread",
    };
  }
  const title = markdownToPlainText(row.title);
  return {
    title,
    secondLine: secondLineWithoutEcho(title, [row.project_name, repeats, time]),
    tone: row.read
      ? null
      : QUESTION_KINDS.has(row.kind)
        ? "question"
        : "unread",
  };
}

export function approvalWords(approval: PendingApproval): {
  title: string;
  secondLine: string;
} {
  return {
    title: approval.title,
    secondLine: joinSecondLine(
      approval.project_name,
      formatRelativeTime(approval.created_at, { style: "narrow" }),
    ),
  };
}
